import { Navigate, Route, Routes } from 'react-router-dom';
import { AppShell } from '@/layout/AppShell';
import { LoginPage } from '@/pages/public/LoginPage';
import { PlatformDashboard } from '@/pages/platform/PlatformDashboard';
import { TenantsPage } from '@/pages/platform/TenantsPage';
import { FacilitiesPage } from '@/pages/platform/FacilitiesPage';
import { TenantSecurityPage } from '@/pages/platform/TenantSecurityPage';
import { AuditViewerPage } from '@/pages/platform/AuditViewerPage';
import { WebhooksPage } from '@/pages/platform/WebhooksPage';
import { MetricsRollupPage } from '@/pages/platform/MetricsRollupPage';
import { SecretsPage } from '@/pages/platform/SecretsPage';
import { ApiMastersPage } from '@/pages/platform/ApiMastersPage';
import { PlatformBridgePage } from '@/pages/platform/PlatformBridgePage';
import { TenantDashboard } from '@/pages/tenant/TenantDashboard';
import { MetricsPage } from '@/pages/tenant/MetricsPage';
import { MyAuditPage } from '@/pages/tenant/MyAuditPage';
import { MyWebhooksPage } from '@/pages/tenant/MyWebhooksPage';
import { SettingsPage } from '@/pages/tenant/SettingsPage';
import { AbhaPage } from '@/pages/tenant/AbhaPage';
import { ApiExplorerPage } from '@/pages/tenant/ApiExplorerPage';
import { ApiReferencePage } from '@/pages/tenant/ApiReferencePage';
import { IntegrationAbhaPage } from '@/pages/tenant/integration-testing/IntegrationAbhaPage';
import { IntegrationFhirPage } from '@/pages/tenant/integration-testing/IntegrationFhirPage';
import { IntegrationConsentPage } from '@/pages/tenant/integration-testing/IntegrationConsentPage';
import { CareContextPage } from '@/pages/tenant/CareContextPage';
import { ConsultationsPage } from '@/pages/tenant/ConsultationsPage';
import { ConsentPage } from '@/pages/tenant/ConsentPage';
import { UsersPage } from '@/pages/tenant/UsersPage';
import { useAuth } from '@/auth/AuthContext';
import { RequirePortal } from './RequirePortal';

/**
 * Top-level route table. Three branches:
 *
 *   /                  — root, redirects to login or the user's portal
 *   /login             — public, redirects logged-in users to their portal
 *   /platform/*        — RequirePortal('platform')
 *   /tenant/*          — RequirePortal('tenant')
 *
 * Inner routes inside each portal mount through AppShell so the sidebar
 * is consistent. The placeholder pages reference the PR they're landing
 * in so a reviewer always sees what's stubbed.
 */
export function AppRoutes() {
  const { isAuthenticated, portal } = useAuth();

  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />

      {/* Root redirect */}
      <Route
        path="/"
        element={
          isAuthenticated && portal
            ? <Navigate to={portal === 'platform' ? '/platform' : '/tenant'} replace />
            : <Navigate to="/login" replace />
        }
      />

      {/* Platform Owner portal */}
      <Route
        path="/platform"
        element={
          <RequirePortal portal="platform">
            <AppShell portal="platform" />
          </RequirePortal>
        }
      >
        <Route index element={<PlatformDashboard />} />
        <Route path="tenants"    element={<TenantsPage />} />
        <Route path="facilities" element={<FacilitiesPage />} />
        <Route path="security"   element={<TenantSecurityPage />} />
        <Route path="audit"      element={<AuditViewerPage />} />
        {/* Legacy direct link — redirect into the merged tab. */}
        <Route path="api-call-logs" element={<AuditViewerPage />} />
        <Route path="webhooks"   element={<WebhooksPage />} />
        <Route path="rollup"     element={<MetricsRollupPage />} />
        <Route path="secrets"    element={<SecretsPage />} />
        <Route path="api-masters" element={<ApiMastersPage />} />
        <Route path="bridge"      element={<PlatformBridgePage />} />
      </Route>

      {/* Tenant Owner portal */}
      <Route
        path="/tenant"
        element={
          <RequirePortal portal="tenant">
            <AppShell portal="tenant" />
          </RequirePortal>
        }
      >
        <Route index element={<TenantDashboard />} />
        <Route path="metrics"      element={<MetricsPage />} />
        <Route path="audit"        element={<MyAuditPage />} />
        {/* Legacy direct link — keep working by redirecting into the merged tab. */}
        <Route path="api-call-logs" element={<MyAuditPage />} />
        <Route path="webhooks"     element={<MyWebhooksPage />} />
        <Route path="settings"     element={<SettingsPage />} />
        <Route path="abha"         element={<AbhaPage />} />
        <Route path="consultations" element={<ConsultationsPage />} />
        <Route path="api-explorer" element={<ApiExplorerPage />} />
        <Route path="api-reference" element={<ApiReferencePage />} />
        <Route path="integration/abha"          element={<IntegrationAbhaPage />} />
        <Route path="integration/fhir-bundles"  element={<IntegrationFhirPage />} />
        <Route path="integration/consent"       element={<IntegrationConsentPage />} />
        <Route path="care-context" element={<CareContextPage />} />
        <Route path="consent"      element={<ConsentPage />} />
        <Route path="users"        element={<UsersPage />} />
      </Route>

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
